import { Box, Button, CircularProgress, Dialog, IconButton, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useQuizAndQuestionbankStore } from '../../../store/quiz.store'
import { observer } from 'mobx-react'
import quizSubmitSuccessfulImg from '../../../assets/illustrations/quiz-completed.svg'
import DataLoadingSpinner from '../../../components/common/DataLoadingSpinner'
import { Icons, isMobileDevice, latexDelimiters, formatDate, time24to12 } from '../../../utils/utilities'
import { toast } from 'react-toastify'
import api from '../../../service/api'
import { useSearchParams } from 'react-router-dom'
import { useSettingsStore } from '../../../store/settings.store'
import Latex from 'react-latex-next'
import TryAgainMessageBlock from '../../../components/common/TryAgainMessageBlock'
import CountCard from '../../../components/common/CountCard'

const StudentQuiz = observer( ( { page = true } ) => {

    const { isMobile, isSmallScreen } = isMobileDevice()
    const SettingsStore = useSettingsStore()

    const [loading, setLoading] = useState( true )
    const [error, setError] = useState( null )
    const [scoresDetails, setScoresDetails] = useState( {} )
    const [startingQuiz, setStartingQuiz] = useState( false )
    const [viewScoresMode, setViewScoresMode] = useState( false )
    const [smallScreenError, setSmallScreenError] = useState( ( isMobile || isSmallScreen ) && SettingsStore?.getUseSettings['restrict_quiz_attempt_on_mobile_devices'] === true )

    const [searchParams, setSearchParams] = useSearchParams()
    const quiz_id = searchParams.get( 'quiz_id' )
    const instance_id = searchParams.get( 'instance_id' )

    const { meeting_id } = useParams()

    const navigate = useNavigate()

    const { palette, border, layout } = useTheme()

    const QuizStore = useQuizAndQuestionbankStore()

    const goBack = () => {
        if ( meeting_id ) {
            setSearchParams( prev => {
                const newValues = new URLSearchParams( prev )
                newValues.delete( 'quiz_id' )
                newValues.delete( 'instance_id' )
                return newValues
            }, { replace: true } )
            return navigate( `/student/hybrid-class/${meeting_id}` )
        }
        return navigate( `/student/home` )
    }

    const startQuiz = async () => {
        try {
            setStartingQuiz( true )
            const { isMobile, isSmallScreen } = isMobileDevice()
            setSmallScreenError( ( isMobile || isSmallScreen ) && SettingsStore?.getUseSettings['restrict_quiz_attempt_on_mobile_devices'] === true )
            const params = {}
            if ( !page ) {
                params.class_type = "hybrid-class"
                params.hybrid_class_id = meeting_id
            }
            const { data: { data: { attempt_id } } } = await api.quiz.startQuiz( quiz_id, instance_id, params )
            if ( meeting_id )
                navigate( `/student/hybrid-class/${meeting_id}/quiz/${quiz_id}/${instance_id}/attempt/${attempt_id}` )
            else
                navigate( `/student/quiz/${quiz_id}/${instance_id}/attempt/${attempt_id}` )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Could't start the quiz! something went wrong." )
        } finally {
            setStartingQuiz( false )
        }
    }

    const fetchScores = async () => {
        try {
            const { data: { data } } = await api.quiz.fetchAttemptScore( quiz_id, instance_id, +QuizStore?.getQuizInstance?.attempt_id )
            setScoresDetails( data )
            setViewScoresMode( true )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Something went wrong whie fetching scores!" )
        }
    }


    const getData = useCallback( async () => {
        if ( Boolean( quiz_id ) && Boolean( instance_id ) && Number.isInteger( parseInt( quiz_id ) ) && Number.isInteger( parseInt( instance_id ) ) ) {
            const quizResponseDetails = await QuizStore.fetchQuizInstanceForStudent( quiz_id, instance_id )

            if ( !quizResponseDetails.success ) {
                return setError( quizResponseDetails )
            } else
                setError( null )
        } else {
            toast( `Invalid quiz URL` )
            navigate( `/student/home` )
        }
        setLoading( false )
    }, [QuizStore, navigate, quiz_id, instance_id] )

    useEffect( () => {
        getData()
    }, [getData] )

    return (
        <Box padding={layout.pagePadding} flexGrow={1} overflow="auto">
            <Box>
                {!loading && +QuizStore?.getQuizInstance?.attempted === 0 && !error && QuizStore.getQuizInstance && <Box gap="10px" display="flex" borderRadius="5px" flexDirection="column">
                    <Box display="flex" gap="10px" alignItems="center">
                        <Typography variant='h3' margin="0" display="flex" height="100%" alignItems="center" padding="10px" width="fit-content" borderRadius="5px" bgcolor={palette.contentBg} component="div">
                            {Icons.default.QuizIconOutlined}
                        </Typography>
                        <Box>
                            <Typography display="flex" alignItems="center" gap="5px" variant="h1" fontWeight="600" component="h1" fontSize="18px"> {QuizStore.getQuizInstance.title}</Typography>
                            <Typography color='textSecondary' display="flex" alignItems="center" gap="5px" variant="body2" fontSize="14px">{Icons.default.User} {QuizStore.getQuizInstance?.creator_data?.name}</Typography>
                            <Typography display="flex" alignItems="center" gap="5px" fontSize="14px" > {Icons.default.Calendar} {formatDate( QuizStore.getQuizInstance.start_date )}, {time24to12( QuizStore.getQuizInstance.start_time )} - {time24to12( QuizStore.getQuizInstance.end_time )}</Typography>
                        </Box>
                    </Box>

                    {smallScreenError && <Typography borderRadius="5px" color='error' display="flex" gap="10px" alignItems="center" bgcolor={palette.error.light + 22} border={`1px solid ${palette.error.main}`} padding="10px" variant='subtitle2' fontSize="14px">
                        {Icons.default.ErrorIcon}
                        Oops! seems like you are using a device with small screen or a mobile device! please use a desktop or laptop to take this quiz.
                    </Typography>}

                    <Box display="grid" gap="10px" width="100%" gridTemplateColumns={{ md: "repeat(3,1fr)", xs: "repeat(2,1fr)" }}>
                        <CountCard icon={Icons.default.Clock} label='Duration' color="error" value={QuizStore.getQuizInstance.quiz_duration} />
                        {QuizStore.getQuizInstance?.subject?.subject_name && <CountCard icon={Icons.default.Subjects} label='Subject' value={QuizStore.getQuizInstance.subject.subject_name} />}
                        <CountCard icon={Icons.default.Click} label='Max Attempt' value={QuizStore.getQuizInstance.maximum_attempts} />
                        <CountCard icon={Icons.default.QuestionMark} label='Questions' value={QuizStore.getQuizInstance.number_of_question} />
                    </Box>
                    <Box>
                        <Typography gutterBottom marginTop="20px" color="secondary.dark" variant='subtitle2'>
                            Topics for the quiz
                        </Typography>
                        <Box display="flex" alignItems="center" gap="10px">
                            {QuizStore?.getQuizInstance?.question_bank_topics.map( topic => (
                                <Box border={border[1]} sx={{ bgcolor: palette.contentBg, borderRadius: "5px", padding: "5px 10px" }} key={topic.id}>
                                    <Typography variant='subtitle2'>{topic.question_count} questions from {topic.name}</Typography>
                                </Box>
                            ) )}
                        </Box>
                    </Box>


                    <Box display="flex" gap="10px" alignItems="center" marginY="20px">
                        {<Button startIcon={startingQuiz && <CircularProgress size={14} />} disabled={smallScreenError || startingQuiz} disableElevation onClick={startQuiz} sx={{ textTransform: "capitalize" }} variant='contained' >
                            {startingQuiz ? "Starting quiz..." : "Start Quiz"}
                        </Button>}
                        {page && <Button startIcon={Icons.default.ArrowBack} color="error" onClick={goBack} sx={{ textTransform: "capitalize" }} variant='outlined' >
                            Back to home
                        </Button>}
                    </Box>
                </Box>}
                {error && <TryAgainMessageBlock code={error?.code} err={error?.message} getData={getData} />}
                {loading && !error && <DataLoadingSpinner waitingMessage="Loading quiz details..." />}
            </Box>

            {!loading && !error && <Dialog open={+QuizStore?.getQuizInstance?.attempted !== 0} slotProps={{
                paper: { className: 'slim-custom-scrollbar', sx: { width: "60%", maxWidth: "600px" } }
            }}>
                {!viewScoresMode && <Box bgcolor={palette.customThemeColor.light} justifyContent="center" flexGrow={1} alignItems="center" display="flex" flexDirection="column" gap="20px" padding="50px">
                    <img width="70%" src={quizSubmitSuccessfulImg} alt="Submitted successfully" />
                    <Box alignItems="center" display="flex" flexDirection="column">
                        <Typography color={palette.customThemeColor.contrastText} fontWeight={600} gutterBottom variant="h5">Congratulation!</Typography>
                        <Typography color={palette.customThemeColor.contrastText} variant="body2">You have successfully completed the quiz</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap="20px" flexWrap="wrap">
                        <Button onClick={fetchScores} sx={{ paddingX: "40px", fontSize: "12px", fontWeight: "bolder", marginTop: "20px", background: 'white', borderColor: "white", '&:hover': { background: "white", borderColor: "white" } }} variant='outlined' color='primary' >View Score</Button>
                        <Button onClick={goBack} sx={{ paddingX: "40px", fontSize: "12px", fontWeight: "bolder", marginTop: "20px", background: 'white', borderColor: "white", '&:hover': { background: "white", borderColor: "white" } }} variant='outlined' color='primary' >{page ? "Back to home" : "Back to class"}</Button>
                    </Box>
                </Box>}

                {viewScoresMode && <Box bgcolor={palette.background.paper} flexGrow={1} display="flex" flexDirection="column">
                    <Box position="sticky" top="0" display="flex" alignItems="center" justifyContent="space-between" borderBottom={border[1]} padding="10px 20px" bgcolor={palette.background.paper}>
                        <Typography variant="h6">{QuizStore.getQuizInstance.title}</Typography>
                        <IconButton size="small" onClick={() => setViewScoresMode( false )}>
                            {Icons.default.CancelIcon}
                        </IconButton>
                    </Box>
                    <Box >
                        <Box bgcolor={palette.contentBg} position="sticky" top="53px" padding="10px 20px" display="flex" alignItems="center" gap="30px">
                            <Typography variant="h6">OVERALL SCORE</Typography>
                            <Typography variant="h6"> <strong>{scoresDetails?.total_score} / {scoresDetails?.max_score}</strong></Typography>
                        </Box>
                        <Box display="flex" gap="20px" alignItems="center" borderBottom={border[1]} padding="20px">
                            <Box display="flex" gap="10px" alignItems="center">
                                <Box sx={{ width: "15px", borderRadius: "50%", height: "15px", border: `2px solid ${palette.success.dark}` }}></Box>
                                <Typography variant='subtitle2' fontSize="12px" >Correct option</Typography>
                            </Box>
                            <Box display="flex" gap="10px" alignItems="center">
                                <Box sx={{ width: "15px", borderRadius: "50%", bgcolor: palette.success.dark, height: "15px", border: `2px solid ${palette.success.dark}` }}></Box>
                                <Typography variant='subtitle2' fontSize="12px" >Answered, correctly</Typography>
                            </Box>
                            <Box display="flex" gap="10px" alignItems="center">
                                <Box sx={{ width: "15px", borderRadius: "50%", bgcolor: palette.error.light, height: "15px", border: `2px solid ${palette.error.dark}` }}></Box>
                                <Typography variant='subtitle2' fontSize="12px" >Answered, but incorrect</Typography>
                            </Box>
                        </Box>

                        <Box display="flex" padding="0 20px 20px 20px" marginTop="30px" flexDirection="column" gap="20px">
                            {Object.keys( scoresDetails?.scores || {} ).map( ( quesId, index ) => {
                                const ques = scoresDetails?.scores[quesId]
                                return <Box borderBottom={border[1]} paddingBottom="20px" key={index}>
                                    {ques.given < 0 ? <Typography padding="5px 10px" marginBottom="10px" borderRadius="5px" width="fit-content" component="p" bgcolor={palette.errorMessage.light + 22} variant='subtitle2' color="errorMessage.main"><em>Not Answered</em></Typography> :
                                        <Typography padding="5px 10px" marginBottom="10px" borderRadius="5px" width="fit-content" component="p" bgcolor={palette.primaryDark.light + 22} variant='subtitle2' color="primaryDark.main"><em>Answered</em></Typography>}
                                    <Box color={palette.contrstText} className='questionbank-wysiwyg-container' fontWeight="bold" marginTop="-5px" fontSize="14px">
                                        <Latex delimiters={latexDelimiters}>{ques.question_text}</Latex>
                                    </Box>
                                    <Typography variant='body2' color="textSecondary"><em>{ques.selection_type}</em></Typography>
                                    <Box marginTop="10px" display="flex" flexDirection="column" gap="10px">
                                        {ques.options.map( ( option, optionIndex ) => {
                                            const isCorrect = ques.selection_type === "single" ? +ques.correct_answer === optionIndex : ques.options_status[optionIndex]
                                            const hasAnswered = ques.selection_type === "single" ? +ques.given === optionIndex : ques.given.includes( optionIndex )
                                            return (
                                                <Box
                                                    padding="10px"
                                                    border={border[1]}
                                                    borderRadius="5px"
                                                    bgcolor={hasAnswered ? ( hasAnswered && isCorrect ) ? palette.success.light + 44 : palette.error.light + 44 : "none"}
                                                    borderColor={isCorrect ? "success.dark" : hasAnswered ? "errorMessage.dark" : palette.borderColor}
                                                    sx={{ borderWidth: isCorrect || hasAnswered ? "1px" : "1px" }}
                                                    display="flex"
                                                    marginBottom="2px"
                                                    alignItems="flex-start"
                                                    gap="10px"
                                                    key={optionIndex}
                                                >
                                                    <Typography padding="2px 2px 0px 2px" variant="subtitle2" fontSize="12px" sx={{ aspectRatio: "1 / 1", width: "22px" }} bgcolor={isCorrect ? "success.light" : hasAnswered ? "error.light" : palette.greyedOut} border={`1px solid ${isCorrect ? "success.light" : hasAnswered ? "error.light" : palette.borderColor}`} display="flex" alignItems="center" justifyContent="center" borderRadius="50%"
                                                        fontWeight={( isCorrect || hasAnswered ) ? "bolder" : "regular"} color={isCorrect ? "success.contrastText" : hasAnswered ? "error.contrastText" : palette.common.font}>{optionIndex + 1}</Typography>
                                                    <Box variant="subtitle2" className='questionbank-wysiwyg-container' color={palette.common.font}>
                                                        <Latex delimiters={latexDelimiters}>{option.option_text}</Latex>
                                                    </Box>
                                                </Box>
                                            )
                                        } )}
                                    </Box>
                                </Box>
                            } )}
                        </Box>
                    </Box>
                </Box>}
            </Dialog>}
        </Box>
    )
} )

export default StudentQuiz
